import { Badge, Box, Button, Divider, Flex, Grid, GridItem, HStack, Heading, Text, chakra } from "@chakra-ui/react";
import { UsersFour } from "@phosphor-icons/react";
import React from "react";
import { formatDate } from "utils";
import DashboardCard from "../../Dashboard/components/DashboardCard";
import AuditRequirementCard from "../../Dashboard/components/Audit/AuditRequirementCard";
import useAirUserRoles from "../../../../utils/AuthUtils";
import { setGlobal } from "../../../../context/global";
import { useNavigate, useSearchParams } from "react-router-dom";
import { routes } from "../../../../config";

const ActiveAuditSummary = ({ auditData, auditStages }) => {
  const { isAuditorOfAudit, isController } = useAirUserRoles();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const stage = auditStages?.find((s) => s.name === auditData?.stageName);

  const gotoFilteredQuestionList = (questionIds, label = "Audit Summary") => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("rtab", 1); // HACK: goes to the DataInputPage to show the filtered list
    // HACK: pass the list of question ids to the DataInputPage via a global variable, as it may be too large for a url-param
    setGlobal("questionIdsFilter", { label, ids: questionIds });
    newParams.set("q", "questionIdsFilter:true");
    setSearchParams(newParams);
  };

  return (
    <>
      <Box p={6} border="1px" borderColor="gray.200" borderRadius="lg" bg="white">
        {/* Header Section */}
        <Flex justify="space-between" align="flex-start" mb={4}>
          <Box>
            <Text color="green" my={2}>
              Active
            </Text>
            <Heading fontSize="lg" color="gray.800" mt={1}>
              {auditData?.stageName}
            </Heading>
            <Text fontSize="sm" color="gray.500">
              {formatDate(auditData?.startDate, false, false)}
              <chakra.span px={2}>-</chakra.span>
              {formatDate(auditData?.endDate, false, false)}
            </Text>
            <HStack spacing={2} align="flex-start" mt={3}>
              <HStack spacing={1} minW="80px" mt={0.5}>
                <UsersFour size={14} color="#718096" />
                <Text fontSize="xs" color="gray.600">Auditors</Text>
              </HStack>
              <HStack spacing={2} flexWrap="wrap">
                {auditData?.auditors?.map((auditor) => (
                  <Badge key={auditor} px={2} py={1} borderRadius="md" fontSize="body.xxs" fontWeight="500" colorScheme="yellow">
                    {auditor}
                  </Badge>
                ))}
              </HStack>
            </HStack>
          </Box>
          {isController() && stage && (
            <Button
              size="sm"
              variant="primary"
              borderRadius="full"
              onClick={() => navigate(`${routes.issbCloseAudit}?stageId=${stage.id}&stageName=${stage.name}`)}
            >
              Close audit
            </Button>
          )}
        </Flex>

        <Divider mb={4} />

        {/*<Text fontSize="sm" color="gray.600" my={2}>*/}
        {/*  Total: {auditData?.totalQuestions} Questions*/}
        {/*</Text>*/}

        {/* Stats Section */}
        {isAuditorOfAudit(auditData?.id) && <AuditRequirementCard toReview={auditData?.toReview} />}

        <Grid templateColumns="repeat(3, 1fr)" gap={4} mb={6}>
          <GridItem>
            {/*{isAuditorOfAudit(auditData?.id) && <AuditRequirementCard toReview={auditData?.toReview} />}*/}
          </GridItem>
          <GridItem />
          <GridItem />
          {/* Review */}
          <GridItem>
            <DashboardCard
              onClick={
                (auditData?.approvedQuestionList?.length || 0) + (auditData?.rejectedQuestions?.length || 0) > 0
                  ? () => gotoFilteredQuestionList(
                      [...(auditData?.approvedQuestionList || []), ...(auditData?.rejectedQuestions || [])],
                      "Reviewed Questions"
                    )
                  : undefined
              }
            >
              <Text fontSize="heading.s" fontWeight="bold">
                Reviewed
              </Text>
              <Heading fontSize="heading.2xl" color="teal.700">
                {(auditData?.approvedQuestionList?.length || 0) + (auditData?.rejectedQuestions?.length || 0)} of{" "}
                {auditData?.totalQuestions || 0}
              </Heading>
            </DashboardCard>
          </GridItem>

          {/* Approved */}
          <GridItem>
            <DashboardCard
              onClick={
                auditData?.approvedQuestionList?.length > 0
                  ? () => gotoFilteredQuestionList(auditData?.approvedQuestionList, "Approved Questions")
                  : undefined
              }
            >
              <Text fontSize="heading.s" fontWeight="bold">
                Approved
              </Text>
              <Heading fontSize="heading.2xl" color="teal.700">
                {auditData?.approvedQuestionList?.length || 0}
              </Heading>
            </DashboardCard>
          </GridItem>

          {/* Declined */}
          <GridItem>
            <DashboardCard
              onClick={
                auditData?.rejectedQuestionList?.length > 0
                  ? () => gotoFilteredQuestionList(auditData?.rejectedQuestionList, "Declined Questions")
                  : undefined
              }
            >
              <Text fontSize="heading.s" fontWeight="bold">
                Declined
              </Text>
              <Heading fontSize="heading.2xl" color="teal.700">
                {auditData?.rejectedQuestionList?.length || 0}
              </Heading>
            </DashboardCard>
          </GridItem>

          {/* Confidence low */}
          <GridItem>
            <DashboardCard
              onClick={
                auditData?.confidenceLowQuestions?.length > 0
                  ? () => gotoFilteredQuestionList(auditData?.confidenceLowQuestions, "Confidence Low Questions")
                  : undefined
              }
            >
              <Box>
                <Badge px={2} py={1} borderRadius="md" fontSize="body.xxs" colorScheme="red">
                  Confidence: Low
                </Badge>
              </Box>
              <Heading fontSize="heading.2xl" color="teal.700">
                {auditData?.confidenceLowQuestions?.length || 0}
              </Heading>
            </DashboardCard>
          </GridItem>

          {/* Confidence medium */}
          <GridItem>
            <DashboardCard
              onClick={
                auditData?.confidenceMediumQuestions?.length > 0
                  ? () => gotoFilteredQuestionList(auditData?.confidenceMediumQuestions, "Confidence Medium Questions")
                  : undefined
              }
            >
              <Box>
                <Badge px={2} py={1} borderRadius="md" fontSize="body.xxs" colorScheme="orange">
                  Confidence: Medium
                </Badge>
              </Box>
              <Heading fontSize="heading.2xl" color="teal.700">
                {auditData?.confidenceMediumQuestions?.length || 0}
              </Heading>
            </DashboardCard>
          </GridItem>

          {/* Confidence high */}
          <GridItem>
            <DashboardCard
              onClick={
                auditData?.confidenceHighQuestions?.length > 0
                  ? () => gotoFilteredQuestionList(auditData?.confidenceHighQuestions, "Confidence High Questions")
                  : undefined
              }
            >
              <Box>
                <Badge px={2} py={1} borderRadius="md" fontSize="body.xxs" colorScheme="green">
                  Confidence: High
                </Badge>
              </Box>
              <Heading fontSize="heading.2xl" color="teal.700">
                {auditData?.confidenceHighQuestions?.length || 0}
              </Heading>
            </DashboardCard>
          </GridItem>
        </Grid>

        <Grid templateColumns="repeat(3, 1fr)" gap={4}></Grid>
      </Box>
    </>
  );
};

export default ActiveAuditSummary;
